/**
 * Interview API
 * Handles interview-related API endpoints
 */

import { executeRequest } from './hooks/useApiQuery';
import type {
  SSEChunkData,
  SSECompleteData,
  SSEConnectedData,
  SSEErrorData,
  SSEMetadataData,
  SSEToolCallData,
  SSEToolResultData,
} from '@/types/sse.types';

declare const __BACKEND_URL__: string | undefined;

const BACKEND_URL =
  (typeof __BACKEND_URL__ !== 'undefined' && __BACKEND_URL__) ||
  'https://interview.1.2.employability.ai';

const API_VERSION = '/api/v1';
const INTERVIEW_ENDPOINT = `${BACKEND_URL}${API_VERSION}/interview`;

export type InterviewStatus = 'not_started' | 'in_progress' | 'completed';
export type InterviewInitType = 'start' | 'resume';

export interface InterviewData {
  interview_status: InterviewStatus;
  user_name?: string;
  user_email?: string;
  type?: string;
  duration?: number;
  version?: string;
  [key: string]: unknown;
}

export interface PrepareInterviewResponse {
  interview_id: string;
  systemPromptStatus: 'preparing' | 'completed';
  interview?: InterviewData;
  resumeFromTimestamp?: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

/**
 * Prepares an interview by calling the /prepare endpoint
 * @param interview_id - The interview ID to prepare
 * @returns Promise resolving to the prepare response with status
 */
export async function prepareInterview(interview_id: string): Promise<PrepareInterviewResponse> {
  const response = await executeRequest<ApiResponse<PrepareInterviewResponse>>({
    url: '/interview/prepare',
    method: 'POST',
    body: {
      interview_id,
    },
  });
  
  // Extract data from the API response wrapper
  return response.data;
}

export interface InterviewStreamPayload {
  interview_id: string;
  user_message?: string;
  timestamp?: string;
  interview_init_type?: InterviewInitType;
  candidate_name?: string;
}

export interface InterviewStreamHandlers {
  onConnected?: (data: SSEConnectedData) => void;
  onChunk?: (data: SSEChunkData) => void;
  onComplete?: (data: SSECompleteData) => void;
  onError?: (data: SSEErrorData) => void;
  onMetadata?: (data: SSEMetadataData) => void;
  onToolCall?: (data: SSEToolCallData) => void;
  onToolResult?: (data: SSEToolResultData) => void;
  onAbort?: () => void;
}

export interface InterviewStreamController {
  abort: () => void;
}

const parseSSEEvents = (
  rawChunk: string,
  dispatch: (event: string, data: unknown, rawData: string) => void,
  bufferRef: { current: string },
) => {
  bufferRef.current += rawChunk;
  const events = bufferRef.current.split('\n\n');
  bufferRef.current = events.pop() ?? '';

  for (const rawEvent of events) {
    if (!rawEvent.trim()) continue;

    const lines = rawEvent.split('\n');
    let eventType = 'message';
    const dataLines: string[] = [];

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.replace('event:', '').trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.replace('data:', '').trim());
      }
    }

    const dataText = dataLines.join('\n');
    let parsedData: unknown = dataText;

    if (dataText) {
      try {
        parsedData = JSON.parse(dataText);
      } catch {
        // ignore parse failures
      }
    }

    dispatch(eventType, parsedData, dataText);
  }
};

/**
 * Stream interview responses over SSE using POST /interview
 */
export async function streamInterview(
  payload: InterviewStreamPayload,
  handlers: InterviewStreamHandlers = {},
): Promise<InterviewStreamController> {
  const abortController = new AbortController();
  let abortNotified = false;
  const headers = new Headers({
    'Content-Type': 'application/json',
  });

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(INTERVIEW_ENDPOINT, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
    signal: abortController.signal,
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(
      `Interview stream failed (${response.status} ${response.statusText})${errorBody ? `: ${errorBody}` : ''}`,
    );
  }

  if (!response.body) {
    throw new Error('Interview stream has no response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const bufferRef = { current: '' };

  const dispatchEvent = (eventType: string, data: unknown, rawData: string) => {
    switch (eventType) {
      case 'connected':
        handlers.onConnected?.(data as SSEConnectedData);
        break;
      case 'chunk':
        handlers.onChunk?.(data as SSEChunkData);
        break;
      case 'complete':
        handlers.onComplete?.(data as SSECompleteData);
        break;
      case 'error':
        handlers.onError?.(data as SSEErrorData);
        break;
      case 'metadata':
        handlers.onMetadata?.(data as SSEMetadataData);
        break;
      case 'tool-call':
        handlers.onToolCall?.(data as SSEToolCallData);
        break;
      case 'tool-result':
        handlers.onToolResult?.(data as SSEToolResultData);
        break;
      default:
        // ignore unhandled SSE event types
    }
  };

  const pump = async () => {
    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Flush any remaining buffered text
          const remaining = decoder.decode();
          if (remaining) {
            parseSSEEvents(remaining, dispatchEvent, bufferRef);
          }
          if (bufferRef.current.trim()) {
            parseSSEEvents('\n\n', dispatchEvent, bufferRef);
          }
          break;
        }

        const chunkText = decoder.decode(value, { stream: true });
        parseSSEEvents(chunkText, dispatchEvent, bufferRef);
      }
    } catch (error) {
      if (abortController.signal.aborted) {
        if (!abortNotified) {
          abortNotified = true;
          handlers.onAbort?.();
        }
        return;
      }
      handlers.onError?.({
        error: 'client_error',
        message: error instanceof Error ? error.message : 'Unknown stream error',
      });
    }
  };

  void pump();

  return {
    abort: () => {
      if (abortNotified) return;
      abortNotified = true;
      abortController.abort();
      handlers.onAbort?.();
    },
  };
}
